import { useMemo, useState } from 'react'
import { Badge, StatusBadge } from '../components/Badge'
import { Icon } from '../components/Icon'
import { PageHeader } from '../components/PageHeader'
import { useUnsavedChangesGuard } from '../components/DraftGuard'
import { chargeCodeOptions, currentWeek, initialTimeEntries, projectOptions } from '../data'
import type { SubmissionStatus, TimeEntry } from '../types'

interface EntryDraft {
  chargeCode: string
  date: string
  hours: string
  note: string
  project: string
}

const defaultDraft: EntryDraft = {
  chargeCode: chargeCodeOptions[0],
  date: '2026-09-09',
  hours: '1',
  note: '',
  project: projectOptions[0]
}

export const TimePage = () => {
  const [entries, setEntries] = useState<TimeEntry[]>(initialTimeEntries)
  const [view, setView] = useState<'Week' | '2 weeks' | 'Month'>('Week')
  const [status, setStatus] = useState<SubmissionStatus>('draft')
  const [isEditorOpen, setEditorOpen] = useState(false)
  const [isSubmitOpen, setSubmitOpen] = useState(false)
  const [attested, setAttested] = useState(false)
  const [zeroHourReason, setZeroHourReason] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  const [draft, setDraft] = useState<EntryDraft>(defaultDraft)
  const [isDirty, setDirty] = useState(false)
  useUnsavedChangesGuard(isDirty)

  const total = useMemo(() => entries.reduce((sum, entry) => sum + entry.hours, 0), [entries])
  const dateEntries = (date: string) => entries.filter((entry) => entry.date === date)
  const setDraftValue = (key: keyof EntryDraft, value: string) => {
    setDraft((current) => ({ ...current, [key]: value }))
    setDirty(true)
  }

  const saveEntry = () => {
    const hours = Number(draft.hours)
    if (!draft.date || !draft.project || !draft.chargeCode || !Number.isFinite(hours) || hours <= 0 || hours > 24) {
      setNotice('Enter a valid date, project, charge code, and 0–24 hours.')
      return
    }
    setEntries((current) => [
      ...current,
      { id: `time-${Date.now()}`, date: draft.date, project: draft.project, chargeCode: draft.chargeCode.split(' · ')[0], hours, note: draft.note }
    ])
    setEditorOpen(false)
    setDirty(true)
    setNotice('Time entry added to this draft.')
    setDraft(defaultDraft)
  }

  const saveDraft = () => {
    setDirty(false)
    setNotice('Draft saved. You can keep editing until you submit it.')
  }

  const submitTimesheet = () => {
    if (!attested) return
    if (total === 0 && !zeroHourReason) {
      setNotice('Select a reason before submitting a zero-hour week.')
      return
    }
    setStatus('submitted')
    setDirty(false)
    setSubmitOpen(false)
    setNotice('Timesheet submitted. It is now locked while awaiting manager review.')
  }

  return (
    <div className="page time-page">
      <PageHeader
        eyebrow="TIME REPORTING"
        title="Your time, in one weekly view."
        description="Monday–Sunday reporting; the prior week is due Monday at noon Eastern."
        actions={<button className="button button-primary" onClick={() => { setEditorOpen(true); setNotice(null) }} disabled={status !== 'draft'}><Icon name="plus" size={17} /> Add time</button>}
      />

      {notice && <div className="inline-notice" role="status"><Icon name="check" size={17} /><span>{notice}</span><button onClick={() => setNotice(null)} aria-label="Dismiss"><Icon name="close" size={16} /></button></div>}

      <section className="timesheet-toolbar" aria-label="Timesheet controls">
        <div className="range-controls"><button className="icon-button" aria-label="Previous period"><Icon name="chevron-left" size={19} /></button><div><span>Reporting period</span><strong>Sep 7 – Sep 13, 2026</strong></div><button className="icon-button" aria-label="Next period"><Icon name="chevron-right" size={19} /></button></div>
        <div className="view-toggle" aria-label="Time period display">
          {(['Week', '2 weeks', 'Month'] as const).map((option) => <button key={option} className={view === option ? 'active' : ''} onClick={() => setView(option)}>{option}</button>)}
        </div>
        <div className="timesheet-status"><StatusBadge status={status} /><strong>{total.toFixed(1)} h</strong></div>
      </section>

      <section className="time-summary-row">
        <div><span>This week</span><strong>{total.toFixed(1)} <small>hours logged</small></strong></div>
        <div className="time-summary-progress"><div><span style={{ width: `${Math.min(100, (total / 40) * 100)}%` }} /></div><small>{Math.max(0, 40 - total).toFixed(1)} hours remaining to reach 40</small></div>
        <div className="time-summary-legend"><span><i className="legend-dot client" /> Client work</span><span><i className="legend-dot internal" /> Internal</span><small>Review warning above 12/day or 60/week</small></div>
      </section>

      <section className="time-grid" aria-label={`${view} time entries`}>
        {currentWeek.map((day) => {
          const dayEntries = dateEntries(day.dateKey)
          const dayTotal = dayEntries.reduce((sum, entry) => sum + entry.hours, 0)
          return (
            <article className={`day-column ${day.today ? 'today' : ''}`} key={day.dateKey}>
              <header><div><span>{day.day}</span><strong>{day.date}</strong></div>{day.today && <Badge tone="accent">Today</Badge>}</header>
              <div className="day-total"><strong>{dayTotal || '—'}</strong><span>{dayTotal ? 'hours' : 'No time'}</span></div>
              <div className="entry-stack">
                {dayEntries.map((entry) => (
                  <button className={`time-entry ${entry.project === 'Internal Operations' ? 'internal' : ''}`} key={entry.id} onClick={() => status === 'draft' && setNotice(`“${entry.project}” is ready to edit in the connected API.`)}>
                    <span className="time-entry-hours">{entry.hours}h</span>
                    <span><strong>{entry.project}</strong><small>{entry.chargeCode}</small></span>
                  </button>
                ))}
                {!dayEntries.length && status === 'draft' && <button className="empty-day" onClick={() => { setDraft((current) => ({ ...current, date: day.dateKey })); setEditorOpen(true) }}><Icon name="plus" size={16} /> Add time</button>}
              </div>
            </article>
          )
        })}
      </section>

      <section className="timesheet-footer-card">
        <div><span className="footer-status-icon"><Icon name={status === 'submitted' ? 'check' : 'file'} size={19} /></span><div><p className="eyebrow">WEEKLY TIMESHEET</p><h2>{status === 'submitted' ? 'Submitted for manager review' : 'Ready when you are'}</h2><p>{status === 'submitted' ? 'You can view the signed submission and its decision history here.' : 'Review your entries, attest that they are accurate, then submit.'}</p></div></div>
        <div className="footer-card-actions">
          {status === 'draft' && <button className="button button-secondary" onClick={saveDraft}>Save draft</button>}
          <button className="button button-dark" onClick={() => setSubmitOpen(true)} disabled={status !== 'draft'}>{status === 'submitted' ? 'Submitted' : 'Review & submit'} <Icon name="arrow-right" size={17} /></button>
        </div>
      </section>

      {isEditorOpen && (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setEditorOpen(false)}>
          <section className="modal-panel entry-modal" role="dialog" aria-modal="true" aria-labelledby="time-entry-heading" onMouseDown={(event) => event.stopPropagation()}>
            <header className="modal-header"><div><p className="eyebrow">NEW TIME ENTRY</p><h2 id="time-entry-heading">Add focused work</h2></div><button className="icon-button" onClick={() => setEditorOpen(false)} aria-label="Close"><Icon name="close" /></button></header>
            <div className="form-grid">
              <label>Date<input type="date" value={draft.date} onChange={(event) => setDraftValue('date', event.target.value)} /></label>
              <label>Hours<input type="number" min="0" max="24" step="0.25" value={draft.hours} onChange={(event) => setDraftValue('hours', event.target.value)} /></label>
              <label className="form-span-2">Project<select value={draft.project} onChange={(event) => setDraftValue('project', event.target.value)}>{projectOptions.map((project) => <option key={project}>{project}</option>)}</select></label>
              <label className="form-span-2">Charge code<select value={draft.chargeCode} onChange={(event) => setDraftValue('chargeCode', event.target.value)}>{chargeCodeOptions.map((code) => <option key={code}>{code}</option>)}</select></label>
              <label className="form-span-2">What did you work on? <span>Optional</span><textarea rows={3} value={draft.note} placeholder="A concise note makes review easy." onChange={(event) => setDraftValue('note', event.target.value)} /></label>
            </div>
            <footer className="modal-footer"><button className="button button-secondary" onClick={() => setEditorOpen(false)}>Cancel</button><button className="button button-primary" onClick={saveEntry}>Add to draft <Icon name="arrow-right" size={17} /></button></footer>
          </section>
        </div>
      )}

      {isSubmitOpen && (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setSubmitOpen(false)}>
          <section className="modal-panel submission-modal" role="dialog" aria-modal="true" aria-labelledby="attest-heading" onMouseDown={(event) => event.stopPropagation()}>
            <span className="submission-icon"><Icon name="file" size={22} /></span>
            <p className="eyebrow">WEEKLY ATTESTATION</p><h2 id="attest-heading">Review and sign your time.</h2>
            <p>You are submitting <strong>{total.toFixed(1)} hours</strong> for Sep 7 – Sep 13. Once submitted, ordinary edits are locked until the timesheet is returned or reopened.</p>
            {total === 0 && <label>Zero-hour reason<select value={zeroHourReason} onChange={(event) => setZeroHourReason(event.target.value)}><option value="">Select a reason</option><option value="holiday">Holiday</option><option value="leave">Leave</option><option value="no_work_performed">No work performed</option></select></label>}
            <label className="attestation-check"><input type="checkbox" checked={attested} onChange={(event) => setAttested(event.target.checked)} /><span>I attest that this time is accurate and represents work I performed.</span></label>
            <div className="modal-footer"><button className="button button-secondary" onClick={() => setSubmitOpen(false)}>Keep editing</button><button className="button button-primary" onClick={submitTimesheet} disabled={!attested}>Sign &amp; submit <Icon name="check" size={17} /></button></div>
          </section>
        </div>
      )}
    </div>
  )
}
